import { Observable } from 'rxjs';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { filter, first } from 'rxjs/operators';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';

import { ErrorsType } from '@toro/common/shared/enum/errors.type';
import { fadeInOut } from '@toro/common/shared/animations/fadeInOut';
import { FormsUtils } from '@toro/common/shared/resources/forms.utils';
import { AppState } from '@toro/common/shared/interfaces/app-state.interface';
import { RoutesTable } from '@toro/common/shared/resources/routes-table.model';
import { customValidators } from '@toro/common/shared/resources/custom-validators';
import { FilesAction } from '@toro/common/shared/state-management/files/files.actions';
import { AgreementsOptions } from '@toro/common/shared/enum/agreement/agreements-options.enum';
import { AgreementsAction } from '@toro/common/shared/state-management/agreements/agreements.action';
import { AgreementsEffects } from '@toro/common/shared/state-management/agreements/agreements.effects';
import { DisposableSafelly } from '@toro/common/shared/components/disposable-safelly/disposable-safelly.component';
import { ApplicationMessagesAction } from '@toro/common/shared/state-management/application-messages/application-message.action';
import { environment } from '@hub-workspace/apps/logged-home/src/environments/environment';

@AutoUnsubscribe()
@Component({
    selector: 'register-intent-form',
    templateUrl: './register-intent.component.html',
    styleUrls: ['./register-intent.component.scss'],
    animations: [fadeInOut],
})
export class RegisterIntentComponent extends DisposableSafelly implements OnInit, OnDestroy {
    public isNewBrand: boolean = environment.isNewBrand
    public intentForm: FormGroup;
    public agreementList: any[] = [];
    public agreements$: Observable<any>;
    public agreementChecked: boolean = false;
    public agreementTitles = { intermediation: '', toroZero: '' };
    public agreement: any;
    public submitForm: boolean = false;

    constructor(
        public fb: FormBuilder,
        public router: Router,
        public formsUtils: FormsUtils,
        private agreementsEffects: AgreementsEffects,
        private store: Store<AppState>,
    ) {

        super();

        this.agreements$ = this.store.select((s) => s.agreements);

        this.intentForm = this.fb.group({
            term: [false, Validators.compose([Validators.required, customValidators.checked])], // Termo de intermediação
            toroZero: [false],
        });

        this.disposableSubscriptions.push(
            this.agreements$.pipe(
                filter(({ agreements }) => Object.values(agreements).length > 0))
                .subscribe(({ agreements }) => {
                    const contractOfIntermediation = agreements[AgreementsOptions.Intermediation.toLowerCase()];
                    const contractOfToroZero = agreements[AgreementsOptions.TermoAdesaoToroZero.toLowerCase()];

                    if (contractOfIntermediation) {
                        this.agreementList = Object.values(agreements);
                        this.agreementTitles.intermediation = contractOfIntermediation.title;
                        this.agreement = contractOfIntermediation;
                    }

                    if (contractOfToroZero) {
                        this.agreementTitles.toroZero = contractOfToroZero.title;
                    }

                }),
        );

        // Sucesso na atualização
        this.disposableSubscriptions.push(

            this.agreementsEffects.acceptMultipleAgreements$
                .pipe(
                    filter((a: any) => a.type.equals(AgreementsAction.acceptMultipleAgreementsSuccess)),
                    first()
                )
                .subscribe(() => {
                    this.router.navigateByUrl(RoutesTable.hubUrls.routes.Suitability.uri);
                }),
        );

        // Erro ao atualizar o user ou ao aceitar o agreement
        this.disposableSubscriptions.push(

            this.agreementsEffects.acceptAgreement$.pipe(
                filter((a: any) => a.type.equals(AgreementsAction.acceptAgreementError)))
                .subscribe(([agreementAction]: any) => {
                    this.intentForm.updateValueAndValidity();
                    switch (agreementAction.payload.type) {
                        case ErrorsType.Error:
                            this.store.dispatch({ type: ApplicationMessagesAction.genericError, payload: agreementAction.payload });
                            break;
                        case ErrorsType.Warning:
                            this.formsUtils.ValidateBackendScroll(agreementAction.payload.value);
                            (this.intentForm as any).validFields(agreementAction.payload.value);
                            break;
                        default:
                            this.store.dispatch({ type: ApplicationMessagesAction.genericError });
                    }
                }),
        );

        this.dispatchesInit();
    }

    public ngOnInit(): void {
        //
    }

    public ngOnDestroy() { return; }

    /**
     * Baixar os termos Intermediation e ToroZero
     */
    public downloadTerm(event: Event) {

        event.preventDefault();

        const terms = [AgreementsOptions.Intermediation.toLowerCase(), AgreementsOptions.TermoAdesaoToroZero.toLowerCase()];

        this.agreementList
            .filter(({ contractGuid }) =>
                (terms[0] === contractGuid.toLowerCase() || terms[1] === contractGuid.toLowerCase()))
            .forEach(({ agreementFile, title }) =>
                this.store.dispatch({
                    type: FilesAction.getFullFile,
                    payload: {
                        id: agreementFile.toLocaleUpperCase(),
                        name: title
                    }
                }));

    }

    /**
     * Método de submissão do formulário
     */
    public registerObjective() {

        this.submitForm = true;

        this.formsUtils.ValidFormBaseSync(this.intentForm, () => {

            const values = this.intentForm.value;

            // Agreements
            this.store.dispatch({
                type: AgreementsAction.acceptMultipleAgreements,
                payload: this.agreementList
                    .filter((term) => !!term.contractGuid)
                    .map((agreement) => (
                        {
                            contractGuid: agreement.contractGuid,
                            versionId: agreement.versionID,
                            status: values.term,
                            agreementId: agreement.id,
                        })),
            });

        }, true);
    }

    /**
     * Dispatches
     */
    private dispatchesInit() {
        this.store.dispatch({
            type: AgreementsAction.getMultipleAgreementsData,
            payload: [
                AgreementsOptions.Intermediation,
                AgreementsOptions.TermoAdesaoGanhaGanha,
                AgreementsOptions.TesouroDireto,
                AgreementsOptions.RendaFixa,
                // AgreementsOptions.TermoAdesaoRlp, TODO: // validar se vai enviar --- fernando.souza
                AgreementsOptions.TermoAdesaoModelo2Risco,
                AgreementsOptions.TermoAdesaoToroZero],
        });
    }

}
